import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, XCircle, ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";

export default function Quiz() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/quizzes/${quizId}`);
      setData(data);
    })();
  }, [quizId]);

  if (!data) return <div className="max-w-3xl mx-auto px-6 py-16 label-eyebrow">Loading quiz…</div>;

  const { module, quiz } = data;

  const submit = async () => {
    setError("");
    if (Object.keys(answers).length < quiz.questions.length) {
      setError("Please answer every question before submitting.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/quizzes/${quizId}/submit`, { answers });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reset = () => { setAnswers({}); setResult(null); setError(""); };

  // Result view
  if (result) {
    return (
      <div className="max-w-[760px] mx-auto px-6 py-16" data-testid={`quiz-result-${quizId}`}>
        <div className="label-eyebrow mb-4">{module.title} · Mastery Result</div>
        <h1 className="font-serif text-6xl text-ink mb-4">
          {result.passed ? "Mastery achieved." : "Not quite — yet."}
        </h1>
        <div className={`text-2xl font-serif mb-8 ${result.passed ? "text-sage" : "text-rust"}`}>
          {result.score}% · {result.correct} of {result.total} correct · {result.passing_score}% required
        </div>

        <div className="border-t border-stone">
          {quiz.questions.map((q, i) => {
            const r = result.per_question.find((x) => x.id === q.id);
            return (
              <div key={q.id} className="border-b border-stone py-6">
                <div className="flex items-start gap-3 mb-2">
                  {r.correct ? <CheckCircle2 size={20} className="text-sage shrink-0 mt-1" strokeWidth={1.5} /> : <XCircle size={20} className="text-rust shrink-0 mt-1" strokeWidth={1.5} />}
                  <div className="flex-1">
                    <div className="label-eyebrow mb-1">Question {i + 1}</div>
                    <div className="font-serif text-xl text-ink leading-snug">{q.prompt}</div>
                    {!r.correct && (
                      <div className="text-sm text-muted-foreground mt-2">
                        Correct answer:{" "}
                        <span className="text-ink">
                          {q.type === "mcq" ? q.options[r.correct_answer] : (r.correct_answer ? "True" : "False")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-4 mt-10">
          <Link to="/dashboard" className="btn-ghost" data-testid="quiz-to-dashboard">
            <ArrowLeft size={14} strokeWidth={1.5} /> Dashboard
          </Link>
          <button onClick={reset} className="btn-ghost" data-testid="quiz-retake">
            <RotateCcw size={14} strokeWidth={1.5} /> Retake
          </button>
          {result.passed && (
            <Link to={`/curriculum/${module.id}`} className="btn-primary" data-testid="quiz-continue">
              Continue <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Quiz form view (focus mode)
  return (
    <div className="max-w-[760px] mx-auto px-6 py-16" data-testid={`quiz-page-${quizId}`}>
      <Link to={`/curriculum/${module.id}`} className="label-eyebrow inline-flex items-center gap-2 mb-6 hover:text-ink" data-testid="quiz-back-link">
        <ArrowLeft size={12} strokeWidth={1.5} /> {module.title}
      </Link>
      <div className="label-eyebrow mb-3">Mastery quiz · {quiz.questions.length} questions · {quiz.passing_score}% to pass</div>
      <h1 className="font-serif text-4xl text-ink mb-12">Demonstrate what you know.</h1>

      {error && <div className="bg-rust/10 border border-rust/40 text-rust px-4 py-3 text-sm mb-8" data-testid="quiz-error">{error}</div>}

      <div className="space-y-12">
        {quiz.questions.map((q, qi) => (
          <fieldset key={q.id} data-testid={`question-${q.id}`}>
            <legend className="label-eyebrow mb-2">Question {qi + 1} of {quiz.questions.length}</legend>
            <div className="font-serif text-2xl text-ink mb-5 leading-snug">{q.prompt}</div>
            <div className="space-y-2">
              {q.type === "mcq" && q.options.map((opt, oi) => {
                const selected = answers[q.id] === oi;
                return (
                  <label
                    key={oi}
                    className={`flex items-start gap-4 p-4 border cursor-pointer transition-all ${selected ? "border-ink bg-cream" : "border-stone bg-white hover:border-ink/60"}`}
                    data-testid={`q-${q.id}-opt-${oi}`}
                  >
                    <input
                      type="radio" name={q.id} className="mt-1 accent-ink"
                      checked={selected}
                      onChange={() => setAnswers({ ...answers, [q.id]: oi })}
                    />
                    <span className="text-ink">{opt}</span>
                  </label>
                );
              })}
              {q.type === "tf" && (
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "True", v: true },
                    { label: "False", v: false },
                  ].map((opt) => {
                    const selected = answers[q.id] === opt.v;
                    return (
                      <button
                        key={opt.label} type="button"
                        onClick={() => setAnswers({ ...answers, [q.id]: opt.v })}
                        className={`p-4 border text-left font-serif text-xl transition-all ${selected ? "border-ink bg-cream text-ink" : "border-stone bg-white text-muted-foreground hover:border-ink/60 hover:text-ink"}`}
                        data-testid={`q-${q.id}-tf-${opt.label.toLowerCase()}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </fieldset>
        ))}
      </div>

      <div className="mt-12 pt-8 border-t border-stone flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {Object.keys(answers).length} of {quiz.questions.length} answered
        </div>
        <button onClick={submit} disabled={busy} className="btn-primary" data-testid="quiz-submit">
          {busy ? "Submitting…" : "Submit answers"} <ArrowRight size={14} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
